/**
 * Helpers for the `widget_sessions/{sessionId}` coordination doc shared with the
 * store-side scanner widget.
 */

import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/Utils/firebase";
import { logError } from "@/Utils/logger";

/**
 * Resets a widget session on share-cancel so the store-side DESKTOP widget
 * returns to its QR screen instead of staying stuck on "follow instructions".
 *
 * Writes `{ progress: "IDLE", activeScan: null }`:
 * - The API server (truetoform_api) streams `widget_sessions.progress` VERBATIM
 *   over SSE (validate-session.middleware.js + session.route.js). The desktop
 *   widget shows "follow instructions" while progress === "STARTED" and only
 *   re-renders the QR once progress is a non-started value. "IDLE" (the API's
 *   documented initial state) is what flips the desktop back to the QR. Writing
 *   "STARTED" here — as the first version of this feature did — left the desktop
 *   stuck on follow-instructions.
 * - `activeScan: null` keeps the session from looking like a completed scan: the
 *   server maps a present/approved scan to progress "READY", and the PWA's own
 *   `/send-avatar` re-entry jumps to `/measurements-sent` when `activeScan` is
 *   set (see `App.tsx`).
 *
 * NOTE: this only resets the DESKTOP (SSE-driven) view. The MOBILE widget decides
 * whether to auto-open / show the "scan shared" UI from the redirect URL params,
 * NOT from this doc — that half is handled by the `ttf-cancelled` flag the cancel
 * redirect adds (see `RedirectProgress`), which the widget reads in
 * `useAutoOpenWidget`.
 *
 * Best-effort and non-blocking: a failed write is logged but never surfaced to
 * the user or awaited by the UI (the redirect countdown gives it time to land).
 * No-ops when `sessionId` is falsy (shares opened without a widget session).
 */
export const resetWidgetSession = async (
  sessionId: string | null | undefined
): Promise<void> => {
  if (!sessionId) return;

  try {
    await updateDoc(doc(db, "widget_sessions", sessionId), {
      progress: "IDLE",
      activeScan: null,
    });
  } catch (error: any) {
    logError("[widgetSession] Failed to reset widget session on cancel", {
      sessionId,
      errorCode: error?.code,
      errorMessage: error?.message,
    });
  }
};
